import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, ChevronDown, Download, FileSpreadsheet, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import type { MeasurementImportSummary } from "@shared/measurement-archive";

interface MeasurementImportHistoryProps {
  projectId: number;
}

const dateFormat = new Intl.DateTimeFormat("de-AT", {
  dateStyle: "medium",
  timeStyle: "short",
});
const numberFormat = new Intl.NumberFormat("de-AT", { maximumFractionDigits: 6 });
const byteFormat = new Intl.NumberFormat("de-AT", { maximumFractionDigits: 1 });

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${byteFormat.format(size / 1024)} KB`;
  return `${byteFormat.format(size / (1024 * 1024))} MB`;
}

export default function MeasurementImportHistory({ projectId }: MeasurementImportHistoryProps) {
  const endpoint = `/api/projects/${projectId}/measurement-imports`;
  const { data, isLoading, isError, refetch, isFetching } = useQuery<MeasurementImportSummary[]>({
    queryKey: [endpoint],
    queryFn: async () => (await apiRequest("GET", endpoint)).json(),
  });

  if (isLoading) {
    return (
      <section aria-label="Aufmaß-Importe werden geladen" className="space-y-3">
        <Skeleton className="h-5 w-44" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="rounded-lg border border-destructive/25 bg-destructive/5 p-4" role="alert">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <h3 className="text-sm font-semibold">Importverlauf nicht verfügbar</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">Die gespeicherten Aufmaßimporte konnten nicht geladen werden.</p>
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()} disabled={isFetching}>
            <RotateCcw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Erneut versuchen
          </Button>
        </div>
      </section>
    );
  }

  if (!data?.length) {
    return (
      <section className="rounded-lg border border-dashed border-bauwerk-teal/30 bg-bauwerk-mint/10 px-4 py-6 text-center">
        <FileSpreadsheet className="mx-auto h-7 w-7 text-bauwerk-teal/70" />
        <h3 className="mt-2 text-sm font-semibold">Noch keine Aufmaßimporte</h3>
        <p className="mt-1 text-xs text-muted-foreground">Bestätigte Importe werden hier mit Quelle und Ergebnis festgehalten.</p>
      </section>
    );
  }

  return (
    <section className="space-y-3" aria-label="Aufmaß-Importverlauf">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Aufmaß-Importverlauf</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{data.length} gespeicherte{data.length === 1 ? "r Import" : " Importe"}</p>
          <p className="mt-1 text-xs text-muted-foreground">Bestätigte Hinweise dokumentieren die bewusste Verwendung der Werte, keine fachliche oder vermessungstechnische Prüfung.</p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={() => void refetch()} disabled={isFetching} aria-label="Importverlauf aktualisieren">
          <RotateCcw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="space-y-2">
        {data.map((record) => {
          const imported = record.rows.filter((row) => row.status === "imported").length;
          const skipped = record.rows.filter((row) => row.status === "skipped").length;
          const downloadedAt = encodeURIComponent(String(record.id));
          return (
            <article key={record.id} className="overflow-hidden rounded-lg border bg-card transition-colors hover:border-bauwerk-teal/35">
              <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start sm:justify-between sm:p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-bauwerk-mint/60 text-bauwerk-teal">
                      <FileSpreadsheet className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold" title={record.fileName}>{record.fileName}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {dateFormat.format(new Date(record.importedAt))}
                        <span className="mx-1.5 text-border">·</span>
                        {formatFileSize(record.fileSize)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
                    <span className="text-muted-foreground">Tabellenblatt</span>
                    <span className="max-w-full truncate font-medium" title={record.sheet}>{record.sheet}</span>
                    <span className="mx-0.5 hidden text-border sm:inline">·</span>
                    <Badge variant="secondary" className="bg-bauwerk-mint/50 text-bauwerk-teal">{imported} übernommen</Badge>
                    <Badge variant="outline">{skipped} übersprungen</Badge>
                  </div>
                </div>
                <a
                  href={`/api/projects/${projectId}/measurement-imports/${downloadedAt}/download`}
                  className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-input bg-background px-2.5 text-xs font-medium transition-colors hover:border-bauwerk-teal/40 hover:bg-bauwerk-mint/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Originaldatei
                </a>
              </div>

              <details className="group border-t">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs text-muted-foreground transition-colors hover:bg-bauwerk-mint/10 sm:px-4 [&::-webkit-details-marker]:hidden">
                  <span>Zeilen und Importdetails <span className="ml-1 font-mono text-[10px]">{record.rows.length}</span></span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <div className="space-y-3 border-t bg-muted/15 p-3 sm:p-4">
                  {record.rows.length > 0 ? (
                    <div className="divide-y rounded-md border bg-background">
                      {record.rows.map((row, index) => (
                        <div key={`${row.rowNumber}-${row.code ?? ""}-${index}`} className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="font-mono text-[10px] text-muted-foreground">{row.sourcePage ? `PDF-Seite ${row.sourcePage} · ` : ""}Z. {row.rowNumber}</span>
                              {row.code && <code className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{row.code}</code>}
                              <span className="text-xs font-medium">{row.name || "Ohne Bezeichnung"}</span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {row.quantity === null ? "Keine Menge" : `${numberFormat.format(row.quantity)} ${row.unit || ""}`.trim()}
                              {row.targetName && <> <span className="mx-1">→</span>Ziel: {row.targetName}</>}
                            </p>
                            {row.note && <p className="mt-1 text-xs text-muted-foreground"><span className="font-medium">Quellnotiz:</span> {row.note}</p>}
                            {row.reason && <p className="mt-1 text-xs text-amber-800">{row.reason}</p>}
                            {!!row.issues.length && <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-destructive">{row.issues.map((issue, issueIndex) => <li key={`${issueIndex}-${issue}`}>{issue}</li>)}</ul>}
                          </div>
                          <Badge variant={row.status === "imported" ? "secondary" : "outline"} className={`w-fit shrink-0 ${row.status === "imported" ? "bg-bauwerk-mint/50 text-bauwerk-teal" : "text-muted-foreground"}`}>
                            {row.status === "imported" ? <><Check className="mr-1 h-3 w-3" />Übernommen</> : "Übersprungen"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">Für diesen Import sind keine Zeilen gespeichert.</p>
                  )}

                  {!!record.confirmedAssumptions.length && (
                    <aside className="rounded-md border border-amber-600/20 bg-amber-50/60 px-3 py-2.5">
                      <h4 className="text-xs font-semibold text-amber-950">Bestätigte Annahmen aus dem Importdialog</h4>
                      <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs leading-relaxed text-amber-950/80">
                        {record.confirmedAssumptions.map((assumption, index) => <li key={`${index}-${assumption}`}>{assumption}</li>)}
                      </ul>
                    </aside>
                  )}
                </div>
              </details>
            </article>
          );
        })}
      </div>
    </section>
  );
}
